/**
 * Where the app reads its translations from.
 *
 * Normally the backend's `/translation/all`, which serves what Weblate has
 * synced into the platform database — that is the source of truth, and it is why
 * a copy fix goes live minutes after it is translated, without a deploy.
 *
 * A PREVIEW is the exception. It exists to show the code on a branch, so it
 * should show that branch's copy: the JSON bundled into the image, not whatever
 * the shared database currently holds. Otherwise a new key added on the branch
 * renders as its raw key, and a string the branch changed shows the old wording.
 */

import { getRuntimeConfig } from 'constants/API/runtimeConfig'

/**
 * True when the bundled translation JSON should be used and no request made.
 *
 * Two independent switches, either of which is enough:
 *   - `REACT_APP_DISABLE_FETCH_TRANSLATIONS=1` — build-time, for running the app
 *     locally against bundled copy.
 *   - `useBundledTranslations` in `/config.json` — runtime, so ONE image can be
 *     an ordinary environment or a preview showing its branch's copy. The loader
 *     only ever sets it for an environment named `preview-*`.
 *
 * Reading the runtime config throws before the bootstrap has fetched it. This is
 * called from i18next's `loadPath`, long after that, but a caller earlier in the
 * cycle (Storybook, a test importing i18n directly) would otherwise take the app
 * down over a preference — so an unavailable config means "fetch", which is what
 * every environment did before this existed.
 */
export const shouldUseBundledTranslations = (): boolean => {
  if (process.env.REACT_APP_DISABLE_FETCH_TRANSLATIONS === '1') return true
  try {
    return getRuntimeConfig().useBundledTranslations
  } catch {
    return false
  }
}
